import type { OrgMember, OrgRow } from '~/gql/organizations/organizations'
import { useMutation, useQuery } from '@apollo/client/react'
import { RoleEnum } from '@zeus/index'
import { Eye, Loader, UserPlus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AccountStatusBadge } from '~/components/AccountStatusBadge'
import { SidePanel } from '~/components/SidePanel'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_IMPERSONATE } from '~/gql/auth/auth'
import {
  GQL_INVITE_ORG_MEMBER,
  GQL_ORGANIZATION_MEMBERS,
  GQL_REMOVE_ORG_MEMBER,
  GQL_SET_ORG_MEMBER_ROLE,
} from '~/gql/organizations/organizations'
import { client } from '~/helpers/apollo'
import { markPlatformImpersonation } from '~/helpers/impersonation/origin'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/protected-loader'

type IProps = {
  open: boolean
  onCancel: () => void
  onChanged: () => void | Promise<unknown>
  org: OrgRow
}

const MEMBER_ROLES = [RoleEnum.Admin, RoleEnum.Formateur, RoleEnum.User]

function OrganizationMembersModal({ open, onCancel, onChanged, org }: IProps) {
  const { t } = useTranslation()
  const { data, loading, refetch } = useQuery(GQL_ORGANIZATION_MEMBERS, {
    client,
    skip: !open,
    fetchPolicy: 'cache-and-network',
    variables: { orgId: org.id },
  })
  const [inviteOrgMember, { loading: inviting }] = useMutation(GQL_INVITE_ORG_MEMBER, { client })
  const [removeOrgMember] = useMutation(GQL_REMOVE_ORG_MEMBER, { client })
  const [setOrgMemberRole] = useMutation(GQL_SET_ORG_MEMBER_ROLE, { client })
  const [impersonateUser] = useMutation(GQL_IMPERSONATE)
  const { setPayload } = useAuth()

  const [email, setEmail] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [role, setRole] = useState<RoleEnum>(RoleEnum.User)
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  // Ligne en cours de changement : le select se fige le temps de l'aller-retour,
  // sinon deux clics rapides envoient deux mutations concurrentes et c'est la
  // dernière réponse arrivée qui gagne, pas le dernier choix fait.
  const [savingRoleId, setSavingRoleId] = useState<string | null>(null)
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null)

  const members = (data?.organizationMembers ?? []) as unknown as OrgMember[]

  const refresh = async () => {
    await refetch()
    await onChanged()
  }

  const onInvite = async () => {
    if (!email.trim() || !firstName.trim() || !lastName.trim()) {
      toast.error(t('organization.member.invite.required'))
      return
    }
    try {
      await inviteOrgMember({
        variables: {
          input: {
            orgId: org.id,
            email: email.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            role,
          },
        },
      })
      toast.success(t('organization.member.invite.success'))
      setEmail('')
      setFirstName('')
      setLastName('')
      setRole(RoleEnum.User)
      await refresh()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      toast.error(t(msg.includes('EMAIL_ALREADY_USED') ? 'organization.member.invite.emailUsed' : 'organization.member.invite.failed'))
    }
  }

  const onChangeRole = async (userId: string, next: RoleEnum) => {
    setSavingRoleId(userId)
    try {
      await setOrgMemberRole({ variables: { input: { orgId: org.id, userId, role: next } } })
      toast.success(t('organization.member.role.success'))
      await refresh()
    } catch (err) {
      // `LAST_ADMIN` mérite son propre message : « échec » seul laisserait le
      // super-admin réessayer sans comprendre ce qui le bloque.
      const msg = err instanceof Error ? err.message : ''
      toast.error(t(msg.includes('LAST_ADMIN')
        ? 'organization.member.role.lastAdmin'
        : 'organization.member.role.failed'))
    } finally {
      setSavingRoleId(null)
    }
  }

  /**
   * Ouvre une session au nom d'un membre de l'organisation (#43), pour
   * diagnostiquer un ticket sans réclamer ses identifiants.
   *
   * ⚠️ Rechargement complet de la page, et non `navigate` : l'opérateur
   * plateforme est monté sous le basename `/o/_system`, et la session émise
   * appartient à une AUTRE organisation. Une navigation côté client garderait
   * `_system` dans l'URL — le routeur ne change de basename qu'au chargement.
   */
  const onImpersonate = async (m: OrgMember) => {
    setImpersonatingId(m.id)
    try {
      const { data } = await impersonateUser({ variables: { userId: m.id } })
      if (data?.impersonateUser.__typename !== 'UserInfo') {
        toast.error(t('bad.impersonate.user'))
        return
      }
      setPayload({
        firstName: data.impersonateUser.firstName,
        lastName: data.impersonateUser.lastName,
        email: data.impersonateUser.email,
        role: data.impersonateUser.roles,
        id: data.impersonateUser.id,
        impersonatorId: data.impersonateUser.impersonatorId,
      })
      markPlatformImpersonation()
      window.location.href = `/o/${org.slug}${getDefaultRoute(data.impersonateUser.roles)}`
    } catch {
      toast.error(t('bad.impersonate.user'))
    } finally {
      setImpersonatingId(null)
    }
  }

  const onRemove = async (userId: string) => {
    try {
      await removeOrgMember({ variables: { orgId: org.id, userId } })
      toast.success(t('organization.member.remove.success'))
      setConfirmingId(null)
      await refresh()
    } catch {
      toast.error(t('organization.member.remove.failed'))
    }
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onCancel}
      size="lg"
      title={t('organization.members.title', { name: org.name })}
    >
      <div className="flex min-h-0 min-w-0 flex-col gap-4">
        {/* Invitation */}
        <div className="rounded-md border border-border p-3">
          <p className="mb-2 text-sm font-medium">{t('organization.member.invite.title')}</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input aria-label={t('common.email')} autoComplete="off" placeholder={t('common.email')} type="email" value={email} onChange={e => setEmail(e.target.value)} />
            <select
              aria-label={t('common.role')}
              value={role}
              onChange={e => setRole(e.target.value as RoleEnum)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            >
              {MEMBER_ROLES.map(r => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
            </select>
            <Input aria-label={t('common.firstname')} placeholder={t('common.firstname')} value={firstName} onChange={e => setFirstName(e.target.value)} />
            <Input aria-label={t('common.lastname')} placeholder={t('common.lastname')} value={lastName} onChange={e => setLastName(e.target.value)} />
          </div>
          <div className="mt-2 flex justify-end">
            <Button size="sm" onClick={onInvite} disabled={inviting}>
              {inviting ? <Loader className="mr-2 size-4 animate-spin" /> : <UserPlus className="mr-2 size-4" />}
              {t('organization.member.invite.submit')}
            </Button>
          </div>
        </div>

        {/* Liste des membres */}
        <div className="overflow-y-auto rounded-md border">
          {loading && members.length === 0
            ? <div className="flex justify-center p-6"><Spinner className="size-6" /></div>
            : members.length === 0
              ? <p className="p-4 text-center text-sm text-muted-foreground">{t('organization.members.empty')}</p>
              : (
                  <Table aria-label={t('organization.members')}>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('common.email')}</TableHead>
                        <TableHead>{t('common.name')}</TableHead>
                        <TableHead>{t('common.role')}</TableHead>
                        <TableHead>{t('common.status')}</TableHead>
                        <TableHead className="text-right">{t('common.action')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {members.map(m => (
                        <TableRow key={m.id}>
                          <TableCell className="max-w-[18rem] truncate font-medium" title={m.email}>
                            {m.email}
                          </TableCell>
                          <TableCell>
                            {m.firstName}
                            {' '}
                            {m.lastName}
                          </TableCell>
                          <TableCell>
                            <select
                              value={m.role}
                              disabled={savingRoleId === m.id}
                              onChange={e => onChangeRole(m.id, e.target.value as RoleEnum)}
                              aria-label={t('common.role')}
                              className="h-8 rounded-md border border-input bg-transparent px-2 text-sm disabled:opacity-50"
                            >
                              {MEMBER_ROLES.map(r => <option key={r} value={r}>{t(`role.${r}`)}</option>)}
                            </select>
                          </TableCell>
                          <TableCell>
                            <AccountStatusBadge status={m.status} />
                          </TableCell>
                          <TableCell className="text-right">
                            {confirmingId === m.id
                              ? (
                                  <div className="flex justify-end gap-1">
                                    <Button variant="destructive" size="sm" onClick={() => onRemove(m.id)}>
                                      {t('common.confirm')}
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                                      {t('common.cancel')}
                                    </Button>
                                  </div>
                                )
                              : (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={impersonatingId === m.id}
                                      onClick={() => onImpersonate(m)}
                                      title={t('organization.member.impersonate.hint')}
                                    >
                                      <Eye className="mr-1 size-4" />
                                      {t('organization.member.impersonate')}
                                    </Button>
                                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmingId(m.id)}>
                                      {t('organization.member.remove')}
                                    </Button>
                                  </div>
                                )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
          {members.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('organization.member.role.hint')}
            </p>
          )}
        </div>
      </div>
    </SidePanel>
  )
}

export default OrganizationMembersModal
